/**
 * Front desk in-app manual. Keep in sync with docs/manuals/FRONT_DESK_MANUAL.md
 */

import { createHelpAssistantManualSection } from "./helpAssistantManualSection.js";

export const FRONT_DESK_MANUAL_INTRO = {
  title: "Front desk guide",
  subtitle:
    "Front desk staff handle scheduling, the patient pipeline, follow-ups, calling workflows, and support tickets. Use the **header** location switcher when your account has more than one site. The sidebar **Settings** gear is owner-only; use the **user menu** (avatar) for **Your Profile** and **Settings**.",
  defaultRoute: "/dashboard",
};

/** @typedef {{ id: string, group: 'menu' | 'settings' | 'more', menuLabel: string, route: string, keywords: string[], summary: string, howToUse: string[] }} FrontDeskManualSection */

/** @type {FrontDeskManualSection[]} */
export const FRONT_DESK_MANUAL_SECTIONS = [
  {
    id: "dashboard",
    group: "menu",
    menuLabel: "Dashboard",
    route: "/dashboard",
    keywords: [
      "home",
      "overview",
      "front desk",
      "reception",
      "call list",
      "call patients",
      "confirmation call",
      "outbound",
      "dial",
      "phone",
      "twilio",
    ],
    summary:
      "Front desk home: today's **Call List** (appointment confirmation calls), **Follow-Up Tasks** with outbound Call buttons, and location-scoped stats.",
    howToUse: [
      "Open **Dashboard** (`/dashboard`).",
      "Set the correct **location** in the header if you work across multiple offices.",
      "Use **Call List** — patients with scheduled appointments today who need a confirmation call. Click the orange **Call** button to dial through your browser (Twilio). After the call, click **Mark confirmed** when the patient confirms.",
      "Use **Follow-Up Tasks** — overdue, due today, and upcoming follow-ups. Click **Call** on a row to reach the patient by phone.",
      "Use **Call history** (link on Call List or sidebar **Call History**) to review calls already completed — not to place new calls.",
    ],
    faqs: [
      {
        question: "How do I call a patient as front desk?",
        answer:
          "Open [[Dashboard|/dashboard]] → **Call List** for appointment confirmations, or **Follow-Up Tasks** for follow-up outreach → click **Call** next to the patient. Calls use in-browser dialing (Twilio). This is not the agent [[Lead Queue|/agent/lead-queue]].",
      },
      {
        question: "Can front desk make outbound calls?",
        answer:
          "Yes. Front desk can call patients from [[Dashboard|/dashboard]] (**Call List** and **Follow-Up Tasks**). [[Call History|/call-history]] is for reviewing past calls only. Agent [[Lead Queue|/agent/lead-queue]] is a separate system for outbound sales/leads.",
      },
      {
        question: "What is the difference between Call List and Call History?",
        answer:
          "**Call List** on [[Dashboard|/dashboard]] = today's patients to call for appointment confirmation. [[Call History|/call-history]] = log of calls already made.",
      },
    ],
  },
  {
    id: "appointments",
    group: "menu",
    menuLabel: "Appointments",
    route: "/appointments",
    keywords: ["schedule", "calendar", "book", "confirm", "recording"],
    summary:
      "Appointment list: confirm visits, view details, and coordinate with consultations.",
    howToUse: [
      "Open **Appointments** (`/appointments`).",
      "Filter by date or location; open rows to update status or notes.",
      "Coordinate with TCs for consultation recording on same-day visits.",
    ],
    faqs: [
      {
        question: "Can front desk start a consultation recording?",
        answer:
          "Recording is for Owner and Treatment Coordinator roles. Front desk can view appointments and coordinate; TCs launch [[Recording|/recording]].",
      },
    ],
  },
  {
    id: "pipeline",
    group: "menu",
    menuLabel: "Pipeline",
    route: "/pipeline",
    keywords: ["opportunities", "cases", "sales", "kanban", "move", "stage"],
    summary:
      "Pipeline kanban: view and update patient cases in treatment plan stages.",
    howToUse: [
      "Go to **Pipeline** (`/pipeline`).",
      "Select a treatment plan; open patient cards to add notes or move stages (notes required on stage change).",
    ],
    faqs: [
      {
        question: "How do I move a patient in the pipeline?",
        answer:
          "[[Pipeline|/pipeline]] → click patient card → select new stage → enter notes → save.",
      },
    ],
  },
  {
    id: "follow-ups",
    group: "menu",
    menuLabel: "Follow-Ups",
    route: "/follow-ups",
    keywords: ["outreach", "cadence", "messages", "calendar", "overdue"],
    summary:
      "Follow-up calendar: scheduled outreach tasks. Complete overdue items from the banner or calendar.",
    howToUse: [
      "Choose **Follow-Ups** (`/follow-ups`).",
      "Use calendar and overdue list; open items to complete or reschedule.",
    ],
    faqs: [
      {
        question: "How do I complete a follow-up?",
        answer:
          "Open [[Follow-Ups|/follow-ups]], find the item, and mark complete with required notes.",
      },
    ],
  },
  {
    id: "call-history",
    group: "menu",
    menuLabel: "Call History",
    route: "/call-history",
    keywords: ["calls", "phone", "dialer", "outbound", "log", "past calls"],
    summary:
      "Review completed or cancelled calls. To place new calls, use **Call List** and **Follow-Up Tasks** on [[Dashboard|/dashboard]] — not Call History.",
    howToUse: [
      "Open **Call History** (`/call-history`).",
      "Review completed or cancelled calls; use filters the page provides.",
      "To call a patient now, go to [[Dashboard|/dashboard]] → **Call List** (confirmations) or **Follow-Up Tasks** → **Call**.",
    ],
    faqs: [
      {
        question: "How do I call patients from Call History?",
        answer:
          "Call History is read-only. To dial patients, open [[Dashboard|/dashboard]] → **Call List** or **Follow-Up Tasks** → click **Call**.",
      },
    ],
  },
  {
    id: "support",
    group: "menu",
    menuLabel: "Support",
    route: "/tickets",
    keywords: ["ticket", "help", "issue"],
    summary: "Organization support tickets.",
    howToUse: [
      "Click **Support** (`/tickets`).",
      "Create or track tickets for IT or success requests.",
    ],
  },
  {
    id: "profile",
    group: "settings",
    menuLabel: "Your Profile",
    route: "/settings/profile",
    keywords: ["password", "avatar", "account"],
    summary: "Personal profile and password (from user menu or direct URL).",
    howToUse: [
      "Click your **avatar** (top right) → **Your Profile** (`/settings/profile`).",
      "Update contact info or password; save changes.",
    ],
  },
  {
    id: "settings-hub",
    group: "settings",
    menuLabel: "Settings (hub)",
    route: "/settings",
    keywords: ["gear", "preferences"],
    summary:
      "Settings landing. Org/team cards are **owner-only**; you may see a minimal hub but can still open **Profile** from the user menu.",
    howToUse: [
      "From the user menu, choose **Settings** (`/settings`) if needed.",
      "Primary self-service for front desk is usually **Your Profile**.",
    ],
  },
  {
    id: "consultations",
    group: "more",
    menuLabel: "Consultations (list)",
    route: "/consultations",
    keywords: ["visits", "clinical", "read-only", "soap", "download", "export"],
    summary:
      "Consultation list (permission-based, not in front-desk sidebar). View visits and export SOAP when allowed.",
    howToUse: [
      "Go to `/consultations` from this manual or a bookmark.",
      "Open a row for detail at `/consultations/:id`.",
      "Bulk SOAP Excel: list page → Download SOAP. Single note: detail → Export SOAP Note.",
    ],
    faqs: [
      {
        question: "How do I download a SOAP report?",
        answer:
          "If you have permission: [[Consultations|/consultations]] → Download SOAP (.xlsx). Single note: open consultation detail → Export SOAP Note (.txt).",
      },
    ],
  },
  {
    id: "my-templates",
    group: "more",
    menuLabel: "My Templates",
    route: "/agent/my-templates",
    keywords: ["sms", "scripts", "voicemail"],
    summary:
      "SMS / template library for assigned locations (same route agents use; allowed for front desk in routing).",
    howToUse: [
      "Open **My Templates** (`/agent/my-templates`).",
      "Manage folders or templates your office uses for outbound messaging.",
    ],
  },
  createHelpAssistantManualSection(),
];

export const FRONT_DESK_MANUAL_GROUPS = {
  menu: {
    label: "Main menu",
    description: "Front-desk sidebar items.",
  },
  settings: {
    label: "Account & settings",
    description: "Profile and hub (sidebar gear is owner-only).",
  },
  more: {
    label: "More features",
    description: "Useful routes not shown as primary sidebar items.",
  },
};

export function filterFrontDeskManualSections(query, groupFilter) {
  const q = (query || "").trim().toLowerCase();
  return FRONT_DESK_MANUAL_SECTIONS.filter((s) => {
    if (groupFilter && groupFilter !== "all" && s.group !== groupFilter) {
      return false;
    }
    if (!q) return true;
    const hay = [
      s.menuLabel,
      s.summary,
      s.route,
      ...s.keywords,
      ...s.howToUse.join(" "),
      ...(s.faqs || []).map((f) => `${f.question} ${f.answer}`).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return hay.includes(q);
  });
}
